import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Compass, Search, Loader2, ExternalLink, MapPin, ChevronLeft, ChevronRight, ShieldQuestion, AlertTriangle, RotateCcw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const CLS = {
  EXISTS: { label: "Kanonisch / EXISTS", cls: "bg-status-success-bg text-status-success border-status-success-border" },
  VERIFIED_MISSING: { label: "Strict Verified Missing", cls: "bg-status-info-bg text-status-info border-status-info-border" },
  NEEDS_ADDRESS_VERIFICATION: { label: "Needs Address Verification", cls: "bg-status-warning-bg text-status-warning border-status-warning-border" },
  NEEDS_RESEARCH: { label: "Needs Research", cls: "bg-fundio-subtle text-fundio-muted border-fundio-border" },
  DUPLICATE: { label: "Duplicate", cls: "bg-fundio-subtle text-fundio-muted border-fundio-border" },
  CONFLICT: { label: "Conflict", cls: "bg-status-danger-bg text-status-danger border-status-danger-border" },
  CLOSED: { label: "Closed", cls: "bg-status-danger-bg text-status-danger border-status-danger-border" },
  INVALID: { label: "Invalid", cls: "bg-fundio-subtle text-fundio-faint border-fundio-border" },
};
const PRIO = {
  A: "bg-status-danger-bg text-status-danger border-status-danger-border",
  B: "bg-status-warning-bg text-status-warning border-status-warning-border",
  C: "bg-fundio-subtle text-fundio-muted border-fundio-border",
};
const PLAN = {
  FREE: "bg-fundio-subtle text-fundio-muted border-fundio-border",
  LIGHT: "bg-status-info-bg text-status-info border-status-info-border",
  WEBPRO: "bg-fundio-gold/12 text-fundio-gold-deep border-fundio-gold/30",
  MULTI: "bg-status-success-bg text-status-success border-status-success-border",
};
const TABS = [
  ["all", "Alle"], ["exists", "Kanonisch"], ["strict_verified_missing", "Strict Verified"],
  ["needs_address", "Needs Address"], ["needs_research", "Needs Research"],
  ["duplicates", "Duplicates"], ["conflicts", "Conflicts"], ["closed", "Closed"], ["invalid", "Invalid"],
];
const DIR_KPIS = [
  ["total_master", "Total Master", (k) => k.total_master],
  ["strict_verified_active", "Strict Verified Active", (k) => k.strict_verified_active],
  ["exists", "Exists", (k) => k.directory?.EXISTS],
  ["strict_verified_missing", "Strict Verified Missing", (k) => k.directory?.VERIFIED_MISSING],
  ["needs_address", "Needs Address Verif.", (k) => k.directory?.NEEDS_ADDRESS_VERIFICATION],
  ["needs_research", "Needs Research", (k) => k.directory?.NEEDS_RESEARCH],
  ["duplicate", "Duplicate", (k) => k.directory?.DUPLICATE],
  ["conflict", "Conflict", (k) => k.directory?.CONFLICT],
  ["closed", "Closed", (k) => k.directory?.CLOSED],
  ["invalid", "Invalid", (k) => k.directory?.INVALID],
];
const SALES_KPIS = [
  ["prio_a", "Sales A", (k) => k.sales_priority?.A],
  ["prio_b", "Sales B", (k) => k.sales_priority?.B],
  ["prio_c", "Sales C", (k) => k.sales_priority?.C],
  ["plan_light", "LIGHT (59.-)", (k) => k.plan_potential?.LIGHT],
  ["plan_webpro", "WEBPRO (115.-)", (k) => k.plan_potential?.WEBPRO],
  ["plan_multi", "MULTI (249.-)", (k) => k.plan_potential?.MULTI],
];

const TONE = {
  ok: "bg-status-success-bg text-status-success border-status-success-border",
  info: "bg-status-info-bg text-status-info border-status-info-border",
  warn: "bg-status-warning-bg text-status-warning border-status-warning-border",
  none: "bg-fundio-subtle text-fundio-muted border-fundio-border",
};
const UNLINKED = { none: "Nicht verknüpft", pending: "Nicht verknüpft · Import vorgemerkt", R6B_STAGED: "Nicht verknüpft · Prüfstapel R6B" };
const SOURCE_HINT = "Historische Quellklassifikation aus dem Master-Quellstand – nicht der aktuelle Import-/Zuordnungsstatus.";
const ASSIGN_HINT = "Gespeicherte Importzuordnung dieser Zeile – keine kanonische Abnahme. Den tatsächlichen Live-Nachtest zeigt „Import und Betrieb“.";

// Gespeicherte Importzuordnung (canonical_match + import_status), unabhängig von der Quellklassifikation.
function assignment(r) {
  const m = r?.canonical_match;
  const s = r?.import_status;
  if (m === "YES" && s === "imported") return { label: "Importiert · zugeordnet", cls: TONE.ok };
  if (m === "YES") return { label: "Zugeordnet · ohne Importvermerk", cls: TONE.info };
  if (s === "imported") return { label: "Importvermerk ohne Zuordnung · prüfen", cls: TONE.warn };
  if (m === "NO" && UNLINKED[s]) return { label: UNLINKED[s], cls: TONE.none };
  return { label: "Status unbekannt", cls: TONE.none };
}

function AssignBadge({ row, testid }) {
  const a = assignment(row);
  return <span data-testid={testid} title={ASSIGN_HINT} className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${a.cls}`}>{a.label}</span>;
}

function errorText(e) {
  const d = e?.response?.data?.detail;
  return (typeof d === "string" ? d : d?.msg) || e?.message || "Unbekannter Fehler";
}

function LoadError({ message, onRetry }) {
  return (
    <div data-testid="az-error" role="alert" className="m-4 flex flex-wrap items-center gap-3 rounded-lg border border-status-danger-border bg-status-danger-bg px-4 py-3 text-sm text-status-danger">
      <AlertTriangle className="h-4 w-4" />
      <span className="flex-1">Master-Datensätze konnten nicht geladen werden: <span data-testid="az-error-message">{message}</span></span>
      <Button data-testid="az-retry" onClick={onRetry} className="border border-fundio-border bg-white text-xs text-fundio-ink hover:border-fundio-gold">
        <RotateCcw className="mr-1 h-4 w-4" />Erneut versuchen
      </Button>
    </div>
  );
}

function Badge({ map, value, testid }) {
  if (!value || value === "-") return <span data-testid={testid} className="text-xs text-fundio-faint">—</span>;
  const cls = typeof map[value] === "string" ? map[value] : map[value]?.cls;
  const label = typeof map[value] === "string" ? value : map[value]?.label || value;
  return <span data-testid={testid} className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${cls || "border-fundio-border text-fundio-muted"}`}>{label}</span>;
}

function Field({ label, value, link }) {
  const empty = value === null || value === undefined || value === "" || value === "-";
  return (
    <div>
      <dt className="text-xs text-fundio-faint">{label}</dt>
      <dd className="text-sm text-fundio-ink break-words">
        {empty ? <span className="italic text-fundio-faint">—</span>
          : link && String(value).startsWith("http")
            ? <a href={value} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-fundio-gold hover:underline">{value}<ExternalLink className="h-3 w-3" /></a>
            : String(value)}
      </dd>
    </div>
  );
}

export default function AzMaster() {
  const [kpis, setKpis] = useState(null);
  const [facets, setFacets] = useState({ cantons: [], tab_counts: {} });
  const [tab, setTab] = useState("all");
  const [filters, setFilters] = useState({ q: "", canton: "", sales_priority: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const seq = useRef(0);
  const pageSize = 50;

  useEffect(() => {
    api.get("/az-master/kpis").then((r) => setKpis(r.data)).catch(() => setKpis(null));
    api.get("/az-master/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const id = ++seq.current;
    setData(null);
    setError(null);
    const params = { tab, page, page_size: pageSize, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
    api.get("/az-master/list", { params })
      .then((r) => { if (id === seq.current) setData(r.data); })
      .catch((e) => { if (id === seq.current) setError(errorText(e)); });
  }, [tab, page, filters]);

  useEffect(() => { load(); }, [load]);
  const changeTab = (key) => { setTab(key); setPage(1); };
  const changeFilter = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div data-testid="az-master-page">
      <PageHeader title="Schweiz A–Z Master" subtitle="26/26-Kantone Master-Verzeichnis · lesende Ansicht der gespeicherten Quelldaten und Importzuordnungen. Keine Schreibaktionen in dieser Ansicht." />

      <div data-testid="az-scope-note" className="mb-5 flex items-center gap-2 rounded-lg border border-status-info-border bg-status-info-bg/50 px-4 py-2 text-xs text-status-info">
        <ShieldQuestion className="h-4 w-4" />
        <span><span className="font-semibold">QUELLSTAND</span> · Klassifikation, Sales-Felder und Kennzahlen stammen aus dem Master-Quellstand{kpis?.version ? ` (Version ${kpis.version}${kpis.updated_at ? `, Stand ${String(kpis.updated_at).slice(0, 10)}` : ""})` : ""} und sind keine Live-Zählung. Der Import-/Zuordnungsstatus ist der je Zeile gespeicherte Stand; den tatsächlichen Live-Nachtest zeigt „Import und Betrieb“.</span>
      </div>

      <p data-testid="az-kpis-directory-title" className="mb-2 text-xs font-semibold uppercase tracking-wider text-fundio-muted">Quellstand · historische Quellklassifikation</p>
      {/* Directory KPIs */}
      <div data-testid="az-kpis-directory" className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {DIR_KPIS.map(([k, label, get]) => (
          <div key={k} data-testid={`az-kpi-${k}`} className="card-surface rounded-xl px-4 py-3">
            <p className="text-xs text-fundio-muted">{label}</p>
            <p className="mt-1 font-display text-2xl font-semibold text-fundio-ink">{kpis ? (get(kpis) ?? 0) : "—"}</p>
          </div>
        ))}
      </div>
      <p data-testid="az-kpis-sales-title" className="mb-2 text-xs font-semibold uppercase tracking-wider text-fundio-muted">Quellstand · Sales-Segmentierung</p>
      {/* Sales KPIs */}
      <div data-testid="az-kpis-sales" className="mb-6 grid grid-cols-3 gap-3 sm:grid-cols-6">
        {SALES_KPIS.map(([k, label, get]) => (
          <div key={k} data-testid={`az-kpi-${k}`} className="card-surface rounded-xl px-4 py-3">
            <p className="text-xs text-fundio-muted">{label}</p>
            <p className="mt-1 font-display text-xl font-semibold text-fundio-ink">{kpis ? (get(kpis) ?? 0) : "—"}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div data-testid="az-tabs" className="mb-4 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs text-fundio-faint">Filter nach historischer Quellklassifikation:</span>
        {TABS.map(([key, label]) => (
          <button key={key} data-testid={`az-tab-${key}`} onClick={() => changeTab(key)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${tab === key
              ? "border-fundio-gold bg-fundio-gold/12 text-fundio-gold-deep"
              : "border-fundio-border bg-white text-fundio-muted hover:border-fundio-gold"}`}>
            {label}<span className="ml-1.5 font-mono text-[10px] text-fundio-faint">{facets.tab_counts?.[key] ?? "·"}</span>
          </button>
        ))}
      </div>

      <Panel testid="az-panel" noPad
        title="Master-Datensätze"
        actions={
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fundio-faint" />
            <Input data-testid="az-search" value={filters.q} onChange={(e) => changeFilter({ q: e.target.value })}
              placeholder="Name, Ort, PLZ, Strasse, Betreiber…" className="input-dark w-64 pl-9" />
          </div>
        }>
        <div className="flex flex-wrap gap-2 border-b border-fundio-border px-4 py-3">
          <select data-testid="az-filter-canton" value={filters.canton} onChange={(e) => changeFilter({ canton: e.target.value })}
            className="rounded-lg border border-fundio-border bg-white px-3 py-1.5 text-sm text-fundio-ink outline-none focus:border-fundio-gold">
            <option value="">Alle Kantone</option>
            {facets.cantons.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="az-filter-prio" value={filters.sales_priority} onChange={(e) => changeFilter({ sales_priority: e.target.value })}
            className="rounded-lg border border-fundio-border bg-white px-3 py-1.5 text-sm text-fundio-ink outline-none focus:border-fundio-gold">
            <option value="">Alle Sales-Prioritäten</option>
            <option value="A">Sales A</option><option value="B">Sales B</option><option value="C">Sales C</option>
          </select>
        </div>

        {error ? (
          <LoadError message={error} onRetry={load} />
        ) : data === null ? (
          <div data-testid="az-loading" className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
        ) : data.items.length === 0 ? (
          <div className="p-4"><EmptyState icon={Compass} title="Keine Datensätze" description="Für die aktuelle Auswahl wurden keine Master-Zeilen gefunden." testid="az-empty" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                  <th className="px-4 py-3">row_id</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Ort</th>
                  <th className="px-4 py-3">Kt.</th><th className="px-4 py-3" title={SOURCE_HINT}>Quellklassifikation (historisch)</th>
                  <th className="px-4 py-3" title={ASSIGN_HINT}>Import-/Zuordnung (gespeichert)</th><th className="px-4 py-3">Typ</th>
                  <th className="px-4 py-3">Sales</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3 text-right">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fundio-border">
                {data.items.map((r) => (
                  <tr key={r.row_id} data-testid={`az-row-${r.row_id}`} onClick={() => setSelected(r)} className="cursor-pointer bg-white transition-colors hover:bg-fundio-subtle">
                    <td className="px-4 py-3 font-mono text-[11px] text-fundio-faint">{r.row_id}</td>
                    <td className="px-4 py-3 font-medium text-fundio-ink">{r.Name}{r.VERIFIED === "YES" && <span className="ml-2 inline-flex rounded border border-status-success-border bg-status-success-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold text-status-success">VERIFIED</span>}</td>
                    <td className="px-4 py-3 text-fundio-muted"><span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-fundio-faint" />{r.Ort || "—"}</span></td>
                    <td className="px-4 py-3 font-mono text-xs text-fundio-muted">{r.Kanton}</td>
                    <td className="px-4 py-3" title={SOURCE_HINT}><Badge map={CLS} value={r.classification} testid={`az-cls-${r.row_id}`} /></td>
                    <td className="px-4 py-3"><AssignBadge row={r} testid={`az-assign-${r.row_id}`} /></td>
                    <td className="px-4 py-3 text-xs text-fundio-muted">{r.OPERATOR_TYPE || "—"}</td>
                    <td className="px-4 py-3"><Badge map={PRIO} value={r.SALES_PRIORITY} testid={`az-prio-${r.row_id}`} /></td>
                    <td className="px-4 py-3"><Badge map={PLAN} value={r.FUNDIO_PLAN_POTENTIAL} testid={`az-plan-${r.row_id}`} /></td>
                    <td className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1 text-sm font-semibold text-fundio-ink">Öffnen <ChevronRight className="h-4 w-4" /></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!error && data && data.total > pageSize && (
          <div data-testid="az-pagination" className="flex items-center justify-between border-t border-fundio-border px-4 py-3 text-sm">
            <span className="text-fundio-muted">{data.total} Datensätze · Seite {page}/{totalPages}</span>
            <div className="flex gap-2">
              <Button data-testid="az-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="border border-fundio-border bg-white text-xs text-fundio-ink hover:border-fundio-gold disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></Button>
              <Button data-testid="az-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="border border-fundio-border bg-white text-xs text-fundio-ink hover:border-fundio-gold disabled:opacity-40"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Panel>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent data-testid="az-detail-sheet" className="w-full overflow-y-auto sm:max-w-lg">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex flex-wrap items-center gap-2 font-display text-fundio-ink">
                  {selected.Name}<AssignBadge row={selected} testid="az-detail-assign" />
                </SheetTitle>
                <SheetDescription data-testid="az-detail-description" className="text-xs text-fundio-muted">
                  Lesende Detailansicht der Master-Zeile {selected.row_id}: gespeicherte Importzuordnung, historische Quellklassifikation und Quellfelder. Keine Schreibaktionen.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-5">
                <div data-testid="az-detail-assignment" className="card-surface rounded-xl p-4">
                  <div className="mb-1 text-sm font-semibold text-fundio-ink">Gespeicherte Importzuordnung</div>
                  <p className="mb-3 text-xs text-fundio-muted">{ASSIGN_HINT}</p>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field label="canonical_match" value={selected.canonical_match} />
                    <Field label="import_status" value={selected.import_status} />
                    <Field label="canonical_tenant_id" value={selected.canonical_tenant_id} />
                    <Field label="canonical_location_id" value={selected.canonical_location_id} />
                  </dl>
                </div>
                <div data-testid="az-detail-source-cls" className="card-surface rounded-xl p-4">
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-sm font-semibold text-fundio-ink">Historische Quellklassifikation<Badge map={CLS} value={selected.classification} testid="az-detail-cls" /></div>
                  <p className="text-xs text-fundio-muted">{SOURCE_HINT}</p>
                </div>
                <div className="card-surface rounded-xl p-4">
                  <div className="mb-3 text-sm font-semibold text-fundio-ink">Standort</div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field label="row_id" value={selected.row_id} />
                    <Field label="Kanton" value={selected.Kanton} />
                    <Field label="Betreiber / Rechtsträger" value={selected["Betreiber/Rechtsträger"]} />
                    <Field label="Parent-Organisation" value={selected["Parent-Organisation"]} />
                    <Field label="Strasse" value={selected.Strasse} />
                    <Field label="PLZ" value={selected.PLZ} />
                    <Field label="Ort" value={selected.Ort} />
                    <Field label="Telefon" value={selected.Telefon} />
                    <Field label="E-Mail" value={selected["E-Mail"]} />
                    <Field label="Website" value={selected.Website} link />
                    <Field label="offizielle Quelle" value={selected["offizielle Quelle"]} link />
                    <Field label="zweite Quelle" value={selected["zweite Quelle"]} link />
                    <Field label="Status" value={selected.Status} />
                    <Field label="source_verified_at" value={selected.source_verified_at} />
                    <Field label="VERIFIED" value={selected.VERIFIED} />
                  </dl>
                </div>
                <div className="card-surface rounded-xl p-4">
                  <div className="mb-3 text-sm font-semibold text-fundio-ink">Sales-Segmentierung</div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Field label="OPERATOR_TYPE" value={selected.OPERATOR_TYPE} />
                    <div><dt className="text-xs text-fundio-faint">SALES_PRIORITY</dt><dd className="mt-0.5"><Badge map={PRIO} value={selected.SALES_PRIORITY} /></dd></div>
                    <div><dt className="text-xs text-fundio-faint">FUNDIO_PLAN_POTENTIAL</dt><dd className="mt-0.5"><Badge map={PLAN} value={selected.FUNDIO_PLAN_POTENTIAL} /></dd></div>
                    <Field label="HAS_WEBSITE" value={selected.HAS_WEBSITE} />
                    <Field label="WEBSITE_QUALITY" value={selected.WEBSITE_QUALITY} />
                    <Field label="HAS_ONLINE_INVENTORY" value={selected.HAS_ONLINE_INVENTORY} />
                    <Field label="HAS_SOCIAL_MEDIA" value={selected.HAS_SOCIAL_MEDIA} />
                    <Field label="MULTI_LOCATION" value={selected.MULTI_LOCATION} />
                    <Field label="CLEAR_CONTACT_PERSON" value={selected.CLEAR_CONTACT_PERSON} />
                    <Field label="OFFERS_CLEARANCE" value={selected.OFFERS_CLEARANCE} />
                    <Field label="OFFERS_PICKUP" value={selected.OFFERS_PICKUP} />
                    <Field label="OFFERS_DELIVERY" value={selected.OFFERS_DELIVERY} />
                  </dl>
                </div>
                {selected.notes && <p className="text-[11px] text-fundio-faint">{selected.notes}</p>}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
